import { useContext, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast as sonnerToast } from 'sonner';
import { Check, Package, Pencil, UserX, X } from 'lucide-react';
import { Tab } from '@/types';
import { Button } from '@/components/ui/button';
import { ConfirmationAlertDialog } from '@/components/ConfirmationAlertDialog';
import { externalOrdersService } from '@/services/externalOrders/externalOrdersService';
import { queryKeys } from '@/lib/queryKeys';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuth } from '@/hooks/useAuth';
import { useInvoice } from '@/hooks/useInvoice';
import { resolveSaleSellerFromActiveOperator } from '@/services/sales/resolveSaleSeller';
import { EditExternalOrderModal } from '@/components/ExternalOrders/EditExternalOrderModal';
import { PaymentMethodSelectorModal } from '@/components/ExternalOrders/PaymentMethodSelectorModal';
import { formatCurrency } from '@/lib/currency';
import { useTabCalculations } from '@/hooks/tabs/useTabCalculations';
import { toTabTotalsOrgSnapshot } from '@/utils/tabTotals';
import type { ExternalOrderStatus } from '@/constants/externalOrders';
import { LanguageProviderContext } from '@/providers/LanguageProvider';

interface ExternalOrderCardProps {
  order: Tab;
  type: 'pending' | 'active';
}

export const ExternalOrderCard = ({ order, type }: ExternalOrderCardProps) => {
  const { translator } = useContext(LanguageProviderContext);
  const { currentOrganization } = useOrganization();
  const { user, activeOperator } = useAuth();
  const { generateDispatchOrder } = useInvoice();
  const queryClient = useQueryClient();
  const {
    calculateItemTotal,
    calculateSubtotal,
    calculateTotal,
    calculateServiceCharge,
    getServiceChargeSettings,
    calculateIva,
    getIvaSettings,
  } = useTabCalculations();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const orgId = currentOrganization?.id ?? '';
  const currency = currentOrganization?.currency ?? 'CRC';
  const subtotal = calculateSubtotal(order);
  const total = calculateTotal(order);
  const discountAmount = subtotal * (order.discount / 100);
  const subtotalAfterDiscount = subtotal - discountAmount;
  const serviceCharge = calculateServiceCharge(subtotalAfterDiscount, order);
  const serviceChargeSettings = getServiceChargeSettings();
  const iva = calculateIva(subtotalAfterDiscount, order.ivaDisabled);
  const ivaSettings = getIvaSettings();
  const isCounterPayment = order.externalMetadata?.paymentLocation === 'counter';
  const customerPhone = order.externalMetadata?.customer_phone;
  const orderType = order.externalMetadata?.orderType;
  const orderTypeLabel =
    orderType === 'pickup'
      ? translator('ExternalOrders.orderTypePickup')
      : orderType === 'delivery'
        ? translator('ExternalOrders.orderTypeDelivery')
        : null;

  const resolveCompleteSeller = () => {
    const seller = resolveSaleSellerFromActiveOperator({ user, activeOperator });
    if (!seller) {
      throw new Error('Seller not found');
    }
    return seller;
  };

  const updateStatus = useMutation({
    mutationFn: ({
      status,
      paymentMethodId,
      estimatedPrepMinutes,
    }: {
      status: string;
      paymentMethodId?: string;
      estimatedPrepMinutes?: number;
    }) => {
      const seller = status === 'completed' ? resolveCompleteSeller() : undefined;
      return externalOrdersService.updateOrderStatus(
        order.id,
        orgId,
        status as ExternalOrderStatus,
        paymentMethodId,
        seller,
        status === 'completed'
          ? {
            organizationSlug: currentOrganization?.slug ?? '',
            digitalInvoicesEnabled: currentOrganization?.digitalInvoicesEnabled,
            currency: currentOrganization?.currency,
            tabTotalsOrg: toTabTotalsOrgSnapshot(currentOrganization),
          }
          : undefined,
        estimatedPrepMinutes,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.externalOrders(orgId) });
      if (variables.status === 'accepted') {
        void generateDispatchOrder({
          id: order.id,
          customerName: order.customerName,
          items: order.items ?? [],
          createdAt: order.createdAt,
        }).catch((dispatchError) => {
          console.error('Error generating dispatch order for external order:', dispatchError);
          sonnerToast.warning(translator('Tabs.dispatchOrderFailed'), {
            description: translator('Tabs.quantityUpdatedButPrintFailed'),
            duration: 4000,
          });
        });
      }
      if (variables.status === 'completed') {
        queryClient.invalidateQueries({ queryKey: queryKeys.salesAll(orgId) });
      }
    },
    onError: (error: Error) => {
      sonnerToast.error(translator('Errors.unexpectedError'), {
        description: error.message,
        duration: 5000,
      });
    },
  });

  const [selectedMinutes, setSelectedMinutes] = useState<number | null>(null);

  const handleAccept = () => {
    if (selectedMinutes === null) return;
    updateStatus.mutate({
      status: 'accepted',
      estimatedPrepMinutes: selectedMinutes,
    });
  };

  const handleReject = () => {
    updateStatus.mutate({ status: 'cancelled' });
  };

  const handleComplete = () => {
    if (isCounterPayment) {
      setShowPaymentModal(true);
      return;
    }
    updateStatus.mutate({ status: 'completed' });
  };

  const handlePaymentMethodSelected = (paymentMethodId: string) => {
    updateStatus.mutate({ status: 'completed', paymentMethodId });
    setShowPaymentModal(false);
  };

  const handleDismiss = () => {
    updateStatus.mutate(
      { status: 'cancelled' },
      { onSuccess: () => setShowDismissDialog(false) },
    );
  };

  const statusLabel =
    order.externalOrderStatus === 'accepted'
      ? translator('ExternalOrders.statusAccepted')
      : order.externalOrderStatus === 'preparing'
        ? translator('ExternalOrders.statusPreparing')
        : order.externalOrderStatus === 'ready'
          ? translator('ExternalOrders.statusReady')
          : order.externalOrderStatus ?? '';

  return (
    <>
      <div className="rounded-lg border bg-card p-4">
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">{order.customerName}</h3>
            {customerPhone && (
              <p className="text-xs text-muted-foreground">{translator('Invoices.customerPhoneLabel')}: {customerPhone}</p>
            )}
            <p className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground">
              {orderTypeLabel && (
                <span className={orderType === 'delivery' ? 'text-blue-600' : undefined}>
                  • {orderTypeLabel}
                </span>
              )}
              <span className="text-orange-600">
                •{' '}
                {isCounterPayment
                  ? translator('ExternalOrders.paymentAtCounter')
                  : translator('ExternalOrders.paymentAtApp')}
              </span>
            </p>
          </div>
          <p className="text-xl font-bold">{formatCurrency(total, currency)}</p>
        </div>

        <div className="mb-4 space-y-2">
          {order.items?.map((item) => (
            <div key={item.id} className="flex justify-between text-sm">
              <span>
                {item.quantity}x {item.product.name}
                {item.discount > 0 && (
                  <span className="ml-2 text-muted-foreground">
                    (-{item.discount}%)
                  </span>
                )}
                {item.notes && (
                  <span className="ml-2 text-muted-foreground">({item.notes})</span>
                )}
              </span>
              <span>{formatCurrency(calculateItemTotal(item), currency)}</span>
            </div>
          ))}
        </div>

        <div className="mb-4 space-y-1 border-t pt-3 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{translator('Global.subTotal')}:</span>
            <span>{formatCurrency(subtotal, currency)}</span>
          </div>
          {order.discount > 0 && (
            <div className="flex justify-between gap-2 text-muted-foreground">
              <span>
                {translator('Global.discount')} ({order.discount}%):
              </span>
              <span>-{formatCurrency(discountAmount, currency)}</span>
            </div>
          )}
          {serviceChargeSettings.enabled && serviceCharge > 0 && (
            <div className="flex justify-between gap-2 text-muted-foreground">
              <span>
                {translator('Tabs.service')} ({serviceChargeSettings.rate}%):
              </span>
              <span>{formatCurrency(serviceCharge, currency)}</span>
            </div>
          )}
          {ivaSettings.enabled && iva > 0 && (
            <div className="flex justify-between gap-2 text-muted-foreground">
              <span>IVA ({ivaSettings.rate}%):</span>
              <span>{formatCurrency(iva, currency)}</span>
            </div>
          )}
          <div className="flex justify-between gap-2 pt-1 font-bold">
            <span>{translator('Global.total')}:</span>
            <span>{formatCurrency(total, currency)}</span>
          </div>
        </div>

        {type === 'pending' ? (
          <div className="space-y-3">
            <div>
              <p className="mb-2 text-sm font-medium">
                {translator('ExternalOrders.estimatedPrepTime')}
              </p>
              <div className="grid grid-cols-4 gap-2">
                {[10, 15, 20, 25].map((minutes) => (
                  <Button
                    key={minutes}
                    type="button"
                    onClick={() => setSelectedMinutes(minutes)}
                    disabled={updateStatus.isPending}
                    variant={selectedMinutes === minutes ? 'default' : 'outline'}
                    size="sm"
                  >
                    {translator('ExternalOrders.minutes', { minutes: String(minutes) })}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={handleReject}
                disabled={updateStatus.isPending}
                variant="destructive"
                className="flex-1"
              >
                <X className="mr-2 h-4 w-4" />
                {translator('ExternalOrders.reject')}
              </Button>
              <Button
                type="button"
                onClick={handleAccept}
                disabled={updateStatus.isPending || selectedMinutes === null}
                className="flex-1"
              >
                <Check className="mr-2 h-4 w-4" />
                {translator('ExternalOrders.accept')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(true)}
              >
                <Pencil className="mr-2 h-4 w-4" />
                {translator('ExternalOrders.editOrder')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
              <span className="text-sm font-medium">{statusLabel}</span>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(true)}
              >
                <Pencil className="mr-2 h-4 w-4" />
                {translator('ExternalOrders.editOrder')}
              </Button>
              <Button
                type="button"
                onClick={() => setShowDismissDialog(true)}
                disabled={updateStatus.isPending}
                variant="outline"
                size="sm"
              >
                <UserX className="mr-2 h-4 w-4" />
                {translator('ExternalOrders.dismissOrder')}
              </Button>
              <Button
                type="button"
                onClick={handleComplete}
                disabled={updateStatus.isPending}
                size="sm"
              >
                <Package className="mr-2 h-4 w-4" />
                {translator('ExternalOrders.markCompleted')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {showEditModal && (
        <EditExternalOrderModal
          key={order.id}
          order={order}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showPaymentModal && (
        <PaymentMethodSelectorModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onSelect={handlePaymentMethodSelected}
          total={total}
        />
      )}

      <ConfirmationAlertDialog
        open={showDismissDialog}
        onOpenChange={setShowDismissDialog}
        title={translator('ExternalOrders.dismissOrderTitle')}
        description={translator('ExternalOrders.dismissOrderDescription')}
        confirmLabel={translator('ExternalOrders.dismissOrder')}
        cancelLabel={translator('Global.cancel')}
        onConfirm={handleDismiss}
        isConfirming={updateStatus.isPending}
        confirmVariant="destructive"
      />
    </>
  );
};
